import { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import useAxios from "axios-hooks";
import type { SubmitHandler } from "react-hook-form";
import { CreateUserDto, UserResDto } from "../../../api/Api";
import { useParams } from "react-router-dom";
import { ClassroomSelect } from "../../../components/input/ClassroomSelect";

export default function MemberForm(props: {
  onSuccess: any;
  defaultValues?: UserResDto;
}) {
  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CreateUserDto>();

  const sendInvitation = useWatch({ control, name: "send_invitation" });
  const params = useParams();

  const [
    {
      data: createMemberData,
      loading: createMemberLoading,
      error: createMemberError,
    },
    createMember,
  ] = useAxios<UserResDto, CreateUserDto>(
    {
      url: "/users",
      method: "POST",
    },
    { manual: true }
  );

  const [
    {
      data: updateMemberData,
      loading: updateMemberLoading,
      error: updateMemberError,
    },
    updateMember,
  ] = useAxios<UserResDto, any>(
    {
      url: `/users/${props?.defaultValues?._id}`,
      method: "PATCH",
    },
    { manual: true }
  );

  const [{ data: searchData, loading: searchLoading }, getClassrooms] =
    useAxios(
      {
        url: "/classrooms",
      },
      { manual: true }
    );

  const [selectedClassroomOptions, setSelectedClassroomOptions] = useState<[]>(
    []
  );

  useEffect(() => {
    const loadDefaults = async () => {
      const ids =
        props.defaultValues?.classrooms?.map(
          (classroom: any) => classroom._id
        ) || [];

      if (ids.length === 0) return;

      const response = await getClassrooms();
      const options = (response?.data?.docs || [])
        .filter((c: any) => ids.includes(c._id))
        .map((c: any) => ({ label: c.name, value: c._id }));

      setSelectedClassroomOptions(options);
      setValue(
        "classroomIds",
        options.map((opt: any) => opt.value)
      );
    };

    loadDefaults();
  }, [props.defaultValues?.classrooms]);

  const onSubmit: SubmitHandler<CreateUserDto> = async (data) => {
    try {
      let response;

      if (props?.defaultValues) {
        response = await updateMember({
          data: {
            classroomIds: data.classroomIds,
          },
        });
      } else {
        if (data.send_invitation) {
          delete data.temporary_password;
        }

        if (params?.classroomId) {
          data.classroomIds = [params.classroomId];
        }

        response = await createMember({
          data: {
            ...data,
            role: "BUSINESS_MEMBER",
          },
        });
      }

      if (response.status === 201) {
        props.onSuccess(response.data);
      }
    } catch (error) {}
  };

  return (
    <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
      {/* Email Field */}
      <label className="form-control w-full">
        <div className="label">
          <span className="label-text">Email</span>
        </div>
        <input
          disabled={props?.defaultValues?.email ? true : false}
          {...register("email", {
            required: props?.defaultValues?.email ? false : true,
          })}
          defaultValue={props?.defaultValues?.email}
          type="text"
          className="input input-bordered input-sm w-full"
        />
        <div className="label">
          {errors?.email?.type === "required" && (
            <span className="label-text-alt text-error">
              This field is required
            </span>
          )}
        </div>
      </label>

      {/* Username Field */}
      <label className="form-control w-full">
        <div className="label">
          <span className="label-text">Username</span>
        </div>
        <input
          disabled={props?.defaultValues?.username ? true : false}
          {...register("username", {
            required: props?.defaultValues?.username ? false : true,
          })}
          type="text"
          defaultValue={props?.defaultValues?.username}
          className="input input-bordered input-sm w-full"
        />
        <div className="label">
          {errors?.username?.type === "required" && (
            <span className="label-text-alt text-error">
              This field is required
            </span>
          )}
        </div>
      </label>

      {/* Classrooms Field (multi-select) */}
      <label
        className={`form-control w-full ${params?.classroomId ? "hidden" : ""}`}
      >
        <div className="label">
          <span className="label-text">Classrooms</span>
        </div>
        <Controller
          name="classroomIds"
          control={control}
          rules={{ required: params?.classroomId ? false : true }}
          render={({ field }) => (
            <ClassroomSelect
              defaultSelectedOptions={props?.defaultValues?.classrooms?.map(
                (classroom: any) => {
                  return {
                    _id: classroom._id,
                    value: classroom._id,
                    label: classroom.name,
                  };
                }
              )}
              value={selectedClassroomOptions}
              onChange={(options: any) => {
                setSelectedClassroomOptions(selectedClassroomOptions);
                field.onChange(options);
              }}
              placeholder="Choose a classroom..."
            />
          )}
        />
        <div className="label">
          {errors?.classroomIds?.type === "required" && (
            <span className="label-text-alt text-error">
              Please select at least one classroom
            </span>
          )}
        </div>
      </label>

      {/* Send Invitation */}
      {!props.defaultValues && (
        <>
          <div className="label">
            <span className="label-text">Send Invitation</span>
          </div>
          <input
            {...register("send_invitation")}
            type="checkbox"
            defaultChecked={false}
            className="checkbox checkbox-sm"
          />
          <div className="label text-sm opacity-80 italic">
            We'll send an email invitation with a temporary password to the
            member you add. If you prefer not to send an invitation, you must
            assign a temporary password to the new account.
          </div>

          {!sendInvitation && (
            <>
              <div className="label">
                <span className="label-text">Temporary Password</span>
              </div>
              <input
                {...register("temporary_password", { required: true })}
                type="text"
                className="input input-bordered input-sm w-full"
              />
              <div className="label">
                {errors?.temporary_password?.type === "required" && (
                  <span className="label-text-alt text-error">
                    This field is required
                  </span>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Submit Button */}
      {/* {!props.defaultValues && ( */}
      <div className="pt-2 w-full ">
        <button className="btn btn-sm btn-primary w-full" type="submit">
          {props.defaultValues ? "Update" : "Create"}
        </button>
      </div>
      {/* )} */}

      {/* Error & Loading States */}
      {createMemberError && (
        <div className="alert alert-error text-sm mt-6 text-center">
          {createMemberError?.response?.data?.message}
        </div>
      )}
      {createMemberLoading && (
        <div className="flex items-center justify-center mt-6">
          <div className="loading loading-spinner" />
        </div>
      )}
    </form>
  );
}
